<template>
  <v-data-table
    :headers          = "referenceTableHeaders"
    :items            = "referenceStore.references"
    class             = "elevation-3"
    mobile-breakpoint = "1000"
    no-data-text      = "Please add three references"
    hide-default-footer
  >
    <template v-slot:top>
      <v-toolbar flat rounded>
        <v-toolbar-title>
          References
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          @click = "newReferenceModal()"
          color  = "primary"
          dark
        >
          Add Reference
        </v-btn>
        <v-dialog
          v-model   = "showReferenceModal"
          max-width = "1100px"
          persistent
        >
          <v-form
            ref="referenceForm"
            lazy-validation
          >
            <v-card>
              <v-card-title class="primary py-3">
                <h2 class="my-0 ml-md-3">
                  {{`Reference #${referenceNumberDisplay}`}}
                </h2>
              </v-card-title>
              <v-card-text class="mt-5 px-sm-1 px-md-2">
                <v-container>
                  <v-row>
                    <v-col sm="12" md="6">
                      <v-text-field
                        v-model = "tempReference.name"
                        label   = "Reference Name"
                        :rules  = "rules.genericFieldRule(64, true)"
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col sm="12" md="6">
                      <v-text-field
                        v-model = "tempReference.address"
                        @blur   = "validateAddress(tempReference.address)"
                        label   = "Reference Address"
                        :rules  = "rules.genericFieldRule(128,
                          true,
                          [addressValidation.isAddressValid]
                        )"
                        :error-messages = "addressValidation.getErrorMessage()"
                        :readonly       = "saving"
                        validate-on-blur
                        dense
                      ></v-text-field>
                    </v-col>
                    <SuggestedAddressComponent
                      v-bind:addressValidation = "addressValidation"
                      v-bind:callbackToUseSuggestedAddress = "
                        () => tempReference.address = addressValidation.getSuggestedAddress()
                      "
                    />
                    <v-col sm="12" md="6">
                      <v-text-field
                        v-model   = "tempReference.phoneNumber"
                        v-mask    = "'###-###-####'"
                        inputmode = "tel"
                        label     = "Reference Phone Number (Optional)"
                        :rules    = "[v => (
                            v == null
                            || v === ''
                            || v.length === 12
                          ) || 'Not a valid phone number'
                        ]"
                        validate-on-blur
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col sm="12" md="6">
                      <v-text-field
                        v-model = "tempReference.jobTitle"
                        label   = "Reference Employment Position"
                        :rules  = "rules.genericFieldRule(50)"
                        validate-on-blur
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col sm="12" md="6">
                      <v-text-field
                        v-model   = "tempReference.yearsAcquainted"
                        v-mask    = "'##'"
                        inputmode = "numeric"
                        label     = "Years Acquainted"
                        :rules    = "[
                          v => !!v || 'Field is Required'
                        ]"
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions class="mx-sm-4">
                <v-spacer></v-spacer>
                <v-btn
                  color     = "text"
                  @click    = "closeReferenceModal()"
                  :disabled = "saving"
                  text
                >
                  Cancel
                </v-btn>
                <v-btn
                  color     = "secondary"
                  @click    = "saveReferenceModal()"
                  :disabled = "saving"
                  :loading  = "saving"
                  text
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-dialog>
      </v-toolbar>
      <v-dialog
        v-model   = "showReferenceDeleteModal"
        max-width = "600px"
      >
        <v-card class="py-3">
          <v-card-title class="text-h5 justify-center">
            <h4
              class = "text-center"
              style = "word-break: break-word"
            >
              Are you sure you want to delete this Reference?
            </h4>
          </v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color  = "primary"
              @click = "hideDeleteReferenceModal()"
            >
              Cancel
            </v-btn>
            <v-btn
              color  = "error"
              @click = "deleteReference()"
            >
              Delete
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-icon
        class  = "ml-1 mr-1"
        @click = "editReferenceModal(item)"
      >
        mdi-pencil
      </v-icon>
      <v-icon
        @click="deleteReferenceModal(item)"
      >
        mdi-delete
      </v-icon>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import Vue from "vue";

import { mapStores              } from 'pinia';
import { useReferenceStore      } from '@/stores/referenceTable';
import SuggestedAddressComponent  from "/src/components/applicationForm/SuggestedAddressComponent.vue";
import {
  PossibleNextAction,
  Reference
} from "@/models/ApplicationForm";
import {
  AddressValidationState,
  ValidationRules
} from "@/models/ApplicationFormClasses";


export default Vue.extend({
  name: "ReferenceTableComponent",

  inject: [
    '$applicationFormService'
  ],

  components: {
    SuggestedAddressComponent
  },

  data() {
    return {
      referenceTableHeaders: [
        {
          text:     'Name',
          align:    'center',
          sortable: false,
          value:    'name'
        },
        {
          text:     'Address',
          align:    'center',
          sortable: false,
          value:    'address'
        },
        {
          text:     'Phone Number',
          align:    'center',
          sortable: false,
          value:    'phoneNumber',
          width:    '125px'
        },
        {
          text:     'Employment Position',
          align:    'center',
          sortable: false,
          value:    'jobTitle'
        },
        {
          text:     'Years Acquainted',
          align:    'center',
          sortable: false,
          value:    'yearsAcquainted'
        },
        {
          text:     'Actions',
          align:    'center',
          sortable: false,
          value:    'actions',
          width:    '90px'
        }
      ],
      tempReference: {
        name:            "",
        address:         "",
        phoneNumber:     null,
        jobTitle:        "",
        yearsAcquainted: ""
      } as Reference,
      showReferenceModal:        false,
      showReferenceDeleteModal:  false,
      currentlyEditing:          false,
      saving:                    false,
      referenceIndex:            -1,
      referenceNumberDisplay:    1,
      addressValidation:         new AddressValidationState(false),
      rules:                     new ValidationRules()
    }
  },

  computed: {
    ...mapStores(useReferenceStore),
  },

  methods: {
    newReferenceModal() {
      this.tempReference = {
        name:            "",
        address:         "",
        phoneNumber:     null,
        jobTitle:        "",
        yearsAcquainted: ""
      } as Reference;
      this.referenceIndex = -1;

      this.resetValidation();

      this.saving = false;
      this.currentlyEditing = false;
      this.showReferenceModal = true;
      this.referenceNumberDisplay = this.referenceStore.references.length + 1;
      this.addressValidation.reset();
    },

    closeReferenceModal() {
      this.tempReference = {
        name:            "",
        address:         "",
        phoneNumber:     null,
        jobTitle:        "",
        yearsAcquainted: ""
      } as Reference;
      this.referenceIndex     = -1;
      this.showReferenceModal = false;
      this.currentlyEditing   = false;
      this.saving             = false;
      this.addressValidation.reset();
    },

    validateReference (): boolean {
      let vForm = this.$refs.referenceForm as Vue & {validate: () => boolean};

      if (vForm == undefined) {
        return false;
      }

      return vForm.validate();
    },

    /**
     * @name validateAddress
     * @desc Determines if the Address fields are valid or not via Google Address Validation API
     */
    validateAddress(addressToValidate: string): void {
      // Only continues if the address is filled out
      if (addressToValidate.length == 0) {
        this.addressValidation.setError("");
        return;
      }

      // Only queries the API if there is no checked address or the provided address has changed
      if (
        this.addressValidation.previousCheckedAddress != null
        && this.addressValidation.previousCheckedAddress == addressToValidate
      ) {
        return;
      }

      this.addressValidation.processRequest(
        this.$applicationFormService.validateAddress({
          address: {
            street:  addressToValidate,
            city:    "",
            state:   null,
            zipcode: "",
          },
          previousResponseId: this.addressValidation.response?.responseId ?? null
        }),
        addressToValidate
      );
    },

    resetValidation (): void {
      let vForm = this.$refs.referenceForm as Vue & {resetValidation: () => void};

      if (vForm != undefined) {
        vForm.resetValidation();
      }
    },

    async saveReferenceModal () {
      this.saving = true;
      await this.addressValidation.hasRequestFinished();

      // Verifies the form is correct before saving and updating
      if (!this.validateReference()) {
        this.saving = false;
        return;
      }

      // Adds a new reference if the index is less than 0 or greater than the array
      if (this.referenceIndex < 0 || this.referenceIndex > this.referenceStore.references.length) {
        // Adds a new reference
        this.referenceStore.addReference(this.tempReference);
      } else {
        // Updates an existing reference
        this.referenceStore.updateReference(this.referenceIndex, this.tempReference);
      }

      this.tempReference = {
        name:            "",
        address:         "",
        phoneNumber:     null,
        jobTitle:        "",
        yearsAcquainted: ""
      } as Reference;
      this.referenceIndex     = -1;
      this.showReferenceModal = false;
      this.saving             = false;

      if (
        !this.currentlyEditing
        && this.referenceStore.references.length < 3
      ) {
        this.newReferenceModal();
      }
    },

    editReferenceModal (reference: Reference) {
      this.referenceIndex = this.referenceStore.references.indexOf(reference);
      this.referenceNumberDisplay = this.referenceIndex + 1;
      this.tempReference  = Object.assign(
        {}, this.referenceStore.references[this.referenceIndex]
      ) as Reference;

      this.resetValidation();

      this.addressValidation.reset(true);
      this.currentlyEditing = true;
      this.showReferenceModal = true;
    },

    deleteReferenceModal (reference: Reference) {
      this.referenceIndex           = this.referenceStore.references.indexOf(reference);
      this.showReferenceDeleteModal = true;
    },

    hideDeleteReferenceModal () {
      this.showReferenceDeleteModal = false;
      this.referenceIndex           = -1;
    },

    deleteReference () {
      if (this.referenceIndex >= 0 && this.referenceIndex < this.referenceStore.references.length) {
        this.referenceStore.removeReference(this.referenceIndex);

        this.referenceIndex           = -1;
        this.showReferenceDeleteModal = false;
      }
    },
  },

});
</script>

<style scoped>

</style>
